import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api, handleAuthError, UserProfile } from '@/lib/api';

interface AuthContextType {
  isLoggedIn: boolean;
  user: UserProfile | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, firstName: string, lastName: string, dateOfBirth: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  refreshUser: () => Promise<void>;
  applyTokenRefresh: (data: { token: string; membershipTier: string; role: string }) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(() => {
    return localStorage.getItem('accessToken') !== null;
  });
  const [user, setUser] = useState<UserProfile | null>(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const userData = await api.getProfile();
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      setIsLoggedIn(true);
    } catch (error) {
      api.logout();
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('user');
      setUser(null);
      setIsLoggedIn(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const userData = await api.getProfile();
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (error) {
      // silently fail on refresh
    }
  }, []);

  const applyTokenRefresh = useCallback((data: { token: string; membershipTier: string; role: string }) => {
    api.setToken(data.token);
    setUser(prev => {
      if (!prev) return prev;
      const updated = { ...prev, membershipTier: data.membershipTier, role: data.role };
      localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Refresh token immediately on mount — catches tier changes when landing back after Stripe checkout
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isLoggedIn) return;
    api.refreshToken().then(result => {
      if (result?.token) applyTokenRefresh(result);
    }).catch(() => {});
  }, []); // intentionally runs once on mount only

  // POINTS-58: Poll every 5min to sync tier changes (upgrades + downgrades) without logout
  useEffect(() => {
    if (!isLoggedIn) return;

    const interval = setInterval(async () => {
      try {
        const result = await api.refreshToken();
        if (!result?.token) return;
        // Use functional updater to compare against current user without stale closure
        setUser(prev => {
          if (!prev) return prev;
          if (result.membershipTier === prev.membershipTier && result.role === prev.role) return prev;
          api.setToken(result.token);
          const updated = { ...prev, membershipTier: result.membershipTier, role: result.role };
          localStorage.setItem('user', JSON.stringify(updated));
          return updated;
        });
      } catch {
        // Silent fail — never log out user on a polling error
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isLoggedIn]);

  // POINTS-62: Refresh token immediately on tab visibility — catches return from Stripe portal
  useEffect(() => {
    if (!isLoggedIn) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const result = await api.refreshToken();
        if (!result?.token) return;
        setUser(prev => {
          if (!prev) return prev;
          if (result.membershipTier === prev.membershipTier && result.role === prev.role) return prev;
          api.setToken(result.token);
          const updated = { ...prev, membershipTier: result.membershipTier, role: result.role };
          localStorage.setItem('user', JSON.stringify(updated));
          return updated;
        });
      } catch {
        // Silent fail
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isLoggedIn]);

  const login = async (email: string, password: string) => {
    try {
      const authResponse = await api.login({ email, password });
      if (!('accessToken' in authResponse)) {
        throw new Error('2FA_REQUIRED');
      }
      // Set basic user data immediately from login response so navigation is instant
      const basicUser: UserProfile = {
        id: authResponse.user.id,
        email: authResponse.user.email,
        firstName: authResponse.user.firstName,
        lastName: authResponse.user.lastName,
        role: authResponse.user.role,
        status: authResponse.user.status,
        membershipTier: null,
        points: 0,
        createdAt: null,
      };
      localStorage.setItem('user', JSON.stringify(basicUser));
      localStorage.setItem('isLoggedIn', 'true');
      setUser(basicUser);
      setIsLoggedIn(true);
      // Fetch full profile in the background to get membershipTier, points, etc.
      api.getProfile().then((userData) => {
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
      }).catch(() => {/* silently fail — basic user data is already set */});
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  };

  const register = async (email: string, password: string, firstName: string, lastName: string, dateOfBirth: string) => {
    try {
      const response = await api.register({ email, password, firstName, lastName, dateOfBirth });
      // Use registration response data directly — getProfile() would 403 for unverified users
      const userData: UserProfile = {
        id: response.user.id,
        email: response.user.email,
        firstName: response.user.firstName,
        lastName: response.user.lastName,
        role: response.user.role,
        status: response.user.status,
        membershipTier: null,
        points: 0,
        createdAt: null,
      };
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('isLoggedIn', 'true');
      setUser(userData);
      setIsLoggedIn(true);
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  };

  const logout = () => {
    api.logout();
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('user');
    localStorage.removeItem('otp_device_token');
    setUser(null);
    setIsLoggedIn(false);
  };

  return (
    <AuthContext.Provider value={{ isLoggedIn, user, isLoading, login, register, logout, checkAuth, refreshUser, applyTokenRefresh }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
